/**
 * Error sanitization utility to prevent leaking implementation details to users.
 * Maps database and internal errors to user-friendly messages while keeping
 * technical details in console logs for debugging.
 */

export const sanitizeError = (error: unknown): string => {
  if (!error) return 'An unexpected error occurred';

  const errorStr = error instanceof Error ? error.message : String(error);

  // Map specific database/auth errors to user-friendly messages
  if (errorStr.includes('row-level security') || errorStr.includes('RLS')) {
    return 'You do not have permission to perform this action';
  }
  if (errorStr.includes('duplicate key') || errorStr.includes('already exists')) {
    return 'This item already exists. Please use a different identifier';
  }
  if (errorStr.includes('violates not-null constraint') || errorStr.includes('cannot be null')) {
    return 'Please fill in all required fields';
  }
  if (errorStr.includes('violates foreign key') || errorStr.includes('foreign key constraint')) {
    return 'Invalid reference. Please check your input';
  }
  if (errorStr.includes('invalid input syntax')) {
    return 'Invalid input format. Please check your data';
  }
  if (errorStr.includes('unique constraint')) {
    return 'This value must be unique. Please use a different value';
  }
  if (errorStr.includes('check constraint')) {
    return 'The provided value is not valid';
  }
  if (errorStr.includes('network') || errorStr.includes('fetch')) {
    return 'Network error. Please check your connection and try again';
  }
  if (errorStr.includes('timeout')) {
    return 'The request timed out. Please try again';
  }
  if (errorStr.includes('storage') || errorStr.includes('bucket')) {
    return 'File operation failed. Please try again';
  }
  if (errorStr.includes('jwt') || errorStr.includes('token')) {
    return 'Session expired. Please sign in again';
  }
  if (errorStr.includes('invalid credentials') || errorStr.includes('Invalid login')) {
    return 'Invalid email or password';
  }

  // Default fallback - don't expose the actual error
  return 'An error occurred. Please try again or contact support';
};

/**
 * Logs errors appropriately based on environment.
 * In production, this could be extended to send to a logging service.
 */
export const logError = (context: string, error: unknown): void => {
  // Always log to console in development
  // In production, you could extend this to send to a logging service
  console.error(`[${context}]:`, error);
};
